// Export produk → template Mass Upload Shopee. Bagian MURNI (tanpa I/O, tanpa
// exceljs) agar bisa diuji unit — pola importExport.ts.
// Perakitan .xlsx-nya ada di src/lib/shopeeExportServer.ts, pembacaan template
// di src/lib/shopeeTemplate.ts.
//
// ── DUA HAL YANG MENGIKAT SELURUH BERKAS INI ─────────────────────────────────
//
// 1. SKU TIDAK BOLEH SALAH. `product_variants.sku` menempel di barcode fisik;
//    satu baris Shopee yang tertukar SKU-nya = salah kirim barang.
//    Karena itu tiap baris diresolusi lewat `option_ids` — IDENTITAS SEBENARNYA
//    varian — bukan lewat `name`, yang cuma label turunan dan sudah terbukti
//    bisa desync (catatan lazyVariants.ts: 1 baris PROD sudah menyimpang dari
//    nama turunannya). SKU tak pernah DIKARANG: kombinasi yang tak punya baris
//    `product_variants` dilewati dan dilaporkan, bukan diberi SKU tebakan.
//
// 2. SHOPEE HANYA 2 TINGKAT VARIASI, WEBSITE 3. Grup 1..n-1 digabung jadi
//    Variasi 1, grup terakhir jadi Variasi 2 (lihat planVariantTiers).
//
// Konsekuensi penting: pemotongan label variasi (batas 14/20 karakter Shopee)
// TIDAK PERNAH bisa merusak SKU, karena SKU datang dari baris varian hasil
// pencocokan option_ids — bukan dari labelnya.

import { resolveProductMediaUrl } from "@/lib/mediaUrl";
import type { ShopeeRowValues } from "@/lib/shopeeTemplate";

// ── Batas & konstanta Shopee ─────────────────────────────────────────────────

/**
 * Batas yang DIBACA LANGSUNG dari baris petunjuk template Shopee (baris 6),
 * bukan tebakan.
 */
export const SHOPEE_LIMITS = {
  nameMin: 5,
  nameMax: 255,
  descMin: 20,
  descMax: 3000,
  /** "Masukkan 1 sampai 14 karakter untuk nama variasi." */
  variationNameMax: 14,
  /** "Masukkan 1 sampai 20 karakter untuk nama pilihan variasi." */
  optionNameMax: 20,
  priceMin: 99,
  priceMax: 150_000_000,
  /** "Batas harga produk termahal dibagi harga harga produk termurah: 7" */
  priceRatioMax: 7,
  stockMax: 10_000_000,
  skuMax: 100,
} as const;

/**
 * Baris data maksimum per file. Template Shopee memasang data-validation-nya
 * pada `…7:…1007` = 1001 baris, jadi 1000 baris data adalah wilayah yang jelas
 * aman. Lebih dari itu file dipecah.
 */
export const SHOPEE_MAX_ROWS_PER_FILE = 1000;

/**
 * Stok yang dipakai untuk produk PREORDER tanpa kuota PO. Produk preorder tak
 * punya stok fisik, sementara Shopee mewajibkan angka stok agar produk bisa
 * dibeli — 0 berarti produk mati begitu diupload. Angka ini SENGAJA konstanta
 * bernama supaya sekali ubah bila pemilik ingin lain.
 */
export const SHOPEE_PREORDER_DEFAULT_STOCK = 100;

/**
 * Berat bawaan (GRAM) untuk varian yang beratnya kosong di database. Shopee
 * MENOLAK baris tanpa berat, jadi sel kosong berarti produknya gagal terupload
 * — bukan sekadar kolom yang perlu dirapikan. Varian yang memakai angka bawaan
 * ini tetap dilaporkan supaya berat aslinya bisa dibetulkan: berat salah =
 * ongkir salah. Konstanta bernama supaya sekali ubah.
 */
export const SHOPEE_DEFAULT_WEIGHT_GRAM = 100;

/** Nilai daftar-validasi template: kolom Produk Berbahaya. */
export const SHOPEE_NOT_DANGEROUS = "No (ID)";
/** Nilai daftar-validasi template: kolom jasa kirim (`channel_id.*`). */
export const SHOPEE_CHANNEL_ACTIVE = "Aktif";

/** Nama variasi bawaan saat produk punya banyak varian tapi tanpa grup. */
export const SHOPEE_FALLBACK_VARIATION_NAME = "Variasi";

// ── Bentuk mentah dari Supabase ──────────────────────────────────────────────

export type ShopeeOptionRow = {
  id: string;
  name: string;
  image_url?: string | null;
  sort_order?: number | null;
};

export type ShopeeGroupRow = {
  id: string;
  name: string;
  sort_order?: number | null;
  use_as_image_primary?: boolean | null;
  variant_options?: ShopeeOptionRow[] | null;
};

export type ShopeeVariantRow = {
  id?: string;
  name: string;
  sku?: string | null;
  option_ids?: string[] | null;
  price_normal?: number | string | null;
  /** Harga tambahan varian (mig 7900) — harga Shopee = dasar + tambahan. */
  price_extra?: number | string | null;
  stock?: number | null;
  po_quota?: number | null;
  weight?: number | string | null;
  is_active?: boolean | null;
  sort_order?: number | null;
};

/**
 * Harga jual utuh satu varian untuk file Shopee = `price_normal` + `price_extra`
 * (mig 7900). Tanpa harga dasar → null (harga menyusul); tambahan tanpa dasar
 * tak pernah berdiri sendiri.
 */
export function shopeeVariantPrice(variant: Pick<ShopeeVariantRow, "price_normal" | "price_extra">): number | null {
  const base = toNum(variant.price_normal);
  if (base === null) return null;
  const extra = toNum(variant.price_extra);
  return base + (extra !== null && extra > 0 ? extra : 0);
}

export type ShopeeMediaRow = {
  media_url?: string | null;
  storage_path?: string | null;
  media_type?: string | null;
  is_cover?: boolean | null;
  sort_order?: number | null;
};

export type ShopeeProductRow = {
  id: string;
  name: string;
  description?: string | null;
  brand?: string | null;
  category?: string | null;
  product_type?: string | null;
  po_limited?: boolean | null;
  sku_prefix?: string | null;
  variant_groups?: ShopeeGroupRow[] | null;
  product_variants?: ShopeeVariantRow[] | null;
  product_media?: ShopeeMediaRow[] | null;
};

// ── Hasil ────────────────────────────────────────────────────────────────────

export type ShopeeWarningKind =
  | "tanpa_varian_layak"
  | "kombinasi_tanpa_varian"
  | "harga_kosong"
  | "harga_di_luar_batas"
  | "rasio_harga"
  | "berat_kosong"
  | "sku_kosong"
  | "deskripsi_otomatis"
  | "deskripsi_pendek"
  | "nama_terlalu_pendek"
  | "foto_sampul_kosong"
  | "foto_varian_tak_lengkap"
  | "varian_tak_terpakai"
  | "produk_terlalu_besar"
  | "sku_ganda";

export type ShopeeWarning = {
  productId: string;
  productName: string;
  kind: ShopeeWarningKind;
  message: string;
};

/** Baris-baris satu produk — TIDAK boleh terpisah antar file. */
export type ShopeeProductBlock = {
  productId: string;
  productName: string;
  rows: ShopeeRowValues[];
};

export type ShopeeBuildResult = {
  blocks: ShopeeProductBlock[];
  warnings: ShopeeWarning[];
  /** Produk yang menghasilkan minimal satu baris. */
  productCount: number;
  rowCount: number;
};

export type ShopeeBuildOptions = {
  /** Kode `channel_id.*` yang ada di template toko ini (jumlahnya per-toko). */
  channelCodes?: string[];
  /** Base URL Supabase untuk merakit URL storage; undefined = pakai env. */
  baseUrl?: string | null;
  /**
   * Hook HARGA per varian (Dropship Shopee: harga normal + markup mitra).
   * Menerima `price_normal` yang sudah lolos cek kosong/≤0, memulangkan harga
   * yang ditulis ke kolom Harga. Dipanggil SEBELUM pemeriksaan batas Shopee
   * (`harga_di_luar_batas`, `rasio_harga`), jadi peringatan menilai harga
   * AKHIR. Tanpa hook = harga normal apa adanya (jalur admin tak berubah).
   */
  priceTransform?: (price: number) => number;
  /**
   * Hook STOK varian READY STOCK (Dropship Shopee: cadangan stok toko).
   * Tidak pernah dipanggil untuk preorder — angka preorder adalah kuota PO /
   * konstanta, bukan stok fisik yang perlu dicadangkan.
   */
  stockTransform?: (stock: number) => number;
};

// ── Utilitas kecil ───────────────────────────────────────────────────────────

function toNum(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function clean(v: string | null | undefined): string {
  return (v ?? "").trim();
}

/**
 * Urutan stabil: `sort_order` dulu, lalu id. Tanpa pemecah-seri id, dua baris
 * ber-sort_order sama bisa bertukar antar-export dan membuat diff file jadi
 * bising tanpa ada yang benar-benar berubah.
 */
function bySortOrder<T extends { sort_order?: number | null; id?: string }>(
  a: T,
  b: T
): number {
  const d = (a.sort_order ?? 0) - (b.sort_order ?? 0);
  if (d !== 0) return d;
  return (a.id ?? "").localeCompare(b.id ?? "");
}

/** Kunci identitas kombinasi: option_ids yang diurutkan. */
export function optionKey(ids: readonly string[]): string {
  return [...ids].sort().join("|");
}

// ── Label variasi ────────────────────────────────────────────────────────────

/**
 * Gabungkan segmen jadi satu label yang muat di `max` karakter.
 *
 * Kalau kepanjangan, jatah dibagi dengan cara "water filling": segmen yang
 * sudah pendek dibiarkan UTUH dan hanya segmen panjang yang dipotong — supaya
 * "Baju" tidak ikut tercincang cuma karena pasangannya "Merah Muda Tua".
 */
export function fitLabel(segments: string[], sep: string, max: number): string {
  const parts = segments.map((s) => clean(s)).filter((s) => s.length > 0);
  if (parts.length === 0) return "";
  const joined = parts.join(sep);
  if (joined.length <= max) return joined;

  const budget = max - sep.length * (parts.length - 1);
  if (budget < parts.length) return joined.slice(0, max).trim();

  const keep = parts.map((p) => p.length);
  let pool = budget;
  let open = parts.map((_, i) => i);
  for (;;) {
    const share = Math.floor(pool / open.length);
    const fits = open.filter((i) => keep[i] <= share);
    if (share <= 0 || fits.length === 0) {
      let extra = pool - share * open.length;
      for (const i of open) {
        keep[i] = Math.max(1, share + (extra-- > 0 ? 1 : 0));
      }
      break;
    }
    for (const i of fits) pool -= keep[i];
    open = open.filter((i) => !fits.includes(i));
    if (open.length === 0) break;
  }
  return parts
    .map((p, i) => p.slice(0, keep[i]).trim())
    .filter((p) => p.length > 0)
    .join(sep)
    .slice(0, max);
}

/**
 * Pastikan label unik dalam satu produk — Shopee menolak "varian atau kombinasi
 * varian yang sama dalam satu produk", dan pemotongan di `fitLabel` bisa
 * membuat dua label berbeda jadi kembar. Pembeda disisipkan sambil tetap muat
 * di `max`. Deterministik: input sama → keluaran sama.
 */
export function uniqueLabels(labels: string[], max: number): string[] {
  const used = new Set<string>();
  return labels.map((raw) => {
    const base = (clean(raw) || "-").slice(0, max);
    if (!used.has(base)) {
      used.add(base);
      return base;
    }
    for (let n = 2; ; n++) {
      const suffix = `~${n}`;
      const head = base.slice(0, Math.max(1, max - suffix.length));
      const cand = `${head}${suffix}`;
      if (!used.has(cand)) {
        used.add(cand);
        return cand;
      }
    }
  });
}

// ── Pelipatan 3 grup → 2 tingkat ─────────────────────────────────────────────

export type ShopeeTierOption = {
  label: string;
  optionIds: string[];
  imageUrl: string | null;
};

export type ShopeeTierPlan = {
  /** Jumlah grup yang benar-benar punya opsi. */
  groupCount: number;
  tier1Name: string;
  tier1: ShopeeTierOption[];
  tier2Name: string;
  /** Kosong bila produk cuma punya 1 grup. */
  tier2: ShopeeTierOption[];
  /** Grup pembawa foto ada di tingkat 1 → foto per-varian bisa dipakai. */
  imageGroupInTier1: boolean;
};

/**
 * Batas panjang label variasi. Bawaan = batas Shopee; export TikTok
 * (tiktokExport.ts) memakai pelipatan yang sama dengan batasnya sendiri.
 */
export type VariantLabelLimits = {
  /** Panjang maksimum NAMA variasi ("Warna"). */
  variationNameMax: number;
  /** Panjang maksimum nama PILIHAN variasi ("Merah"). */
  optionNameMax: number;
};

/**
 * Petakan grup varian website → dua tingkat variasi Shopee.
 *
 * | jumlah grup | Variasi 1              | Variasi 2 |
 * |-------------|------------------------|-----------|
 * | 0           | —                      | —         |
 * | 1           | G1                     | —         |
 * | 2           | G1                     | G2        |
 * | >= 3        | kartesius G1…G(n-1)    | Gn        |
 *
 * Contoh 3 grup (jenis/warna/size) → Variasi 1 "Jenis,Warna" berisi
 * `baju merah · baju kuning · celana merah · celana kuning`, Variasi 2 "Size"
 * berisi `M · L`. Grup PERTAMA jadi loop terluar supaya urutannya terbaca wajar.
 *
 * Grup tanpa opsi dibuang: tak ada kombinasi yang bisa memuatnya, dan
 * membiarkannya hanya akan menggeser perhitungan tingkat.
 */
export function planVariantTiers(
  groups: ShopeeGroupRow[] | null | undefined,
  limits: VariantLabelLimits = SHOPEE_LIMITS
): ShopeeTierPlan {
  const sorted = [...(groups ?? [])]
    .sort(bySortOrder)
    .map((g) => ({
      ...g,
      options: [...(g.variant_options ?? [])]
        .sort(bySortOrder)
        .filter((o) => clean(o.name).length > 0),
    }))
    .filter((g) => g.options.length > 0);

  const empty: ShopeeTierPlan = {
    groupCount: 0,
    tier1Name: "",
    tier1: [],
    tier2Name: "",
    tier2: [],
    imageGroupInTier1: false,
  };
  if (sorted.length === 0) return empty;

  const hasTier2 = sorted.length >= 2;
  const tier1Groups = hasTier2 ? sorted.slice(0, -1) : sorted;
  const tier2Group = hasTier2 ? sorted[sorted.length - 1] : null;

  // Grup pembawa foto: yang ditandai `use_as_image_primary`; kalau tak ada yang
  // ditandai, grup tingkat-1 PERTAMA yang SEMUA opsinya berfoto (Shopee menuntut
  // foto per-varian diisi semua atau tidak sama sekali, jadi grup setengah-
  // berfoto memang tak berguna).
  const marked = sorted.find((g) => g.use_as_image_primary === true);
  const imageGroup =
    marked ??
    tier1Groups.find((g) => g.options.every((o) => clean(o.image_url).length > 0)) ??
    null;
  const imageGroupInTier1 =
    imageGroup !== null && tier1Groups.some((g) => g.id === imageGroup.id);

  // Kartesius tingkat 1, grup pertama sebagai loop TERLUAR.
  let combos: { segments: string[]; optionIds: string[]; imageUrl: string | null }[] =
    [{ segments: [], optionIds: [], imageUrl: null }];
  for (const g of tier1Groups) {
    const isImageGroup = imageGroupInTier1 && imageGroup !== null && g.id === imageGroup.id;
    const next: typeof combos = [];
    for (const acc of combos) {
      for (const o of g.options) {
        next.push({
          segments: [...acc.segments, clean(o.name)],
          optionIds: [...acc.optionIds, o.id],
          imageUrl: isImageGroup ? clean(o.image_url) || null : acc.imageUrl,
        });
      }
    }
    combos = next;
  }

  const tier1Labels = uniqueLabels(
    combos.map((c) => fitLabel(c.segments, " ", limits.optionNameMax)),
    limits.optionNameMax
  );
  const tier1: ShopeeTierOption[] = combos.map((c, i) => ({
    label: tier1Labels[i],
    optionIds: c.optionIds,
    imageUrl: c.imageUrl,
  }));

  const tier2Labels = tier2Group
    ? uniqueLabels(
        tier2Group.options.map((o) =>
          fitLabel([o.name], "", limits.optionNameMax)
        ),
        limits.optionNameMax
      )
    : [];
  const tier2: ShopeeTierOption[] = (tier2Group?.options ?? []).map((o, i) => ({
    label: tier2Labels[i],
    optionIds: [o.id],
    imageUrl: null,
  }));

  return {
    groupCount: sorted.length,
    tier1Name: fitLabel(
      tier1Groups.map((g) => g.name),
      ",",
      limits.variationNameMax
    ),
    tier1,
    tier2Name: tier2Group
      ? fitLabel([tier2Group.name], "", limits.variationNameMax)
      : "",
    tier2,
    imageGroupInTier1,
  };
}

// ── Deskripsi ────────────────────────────────────────────────────────────────

/**
 * Deskripsi cadangan saat `products.description` kosong / < 20 karakter
 * (field-nya memang "opsional" di ProductForm, sedangkan Shopee mewajibkan
 * 20–3000).
 *
 * DIRAKIT DARI DATA PRODUK ITU SENDIRI — nama, merek, kategori, dan daftar
 * varian yang benar-benar ada — bukan kalimat pemasaran generik yang sama untuk
 * semua toko. Produk yang memakainya tetap dilaporkan supaya bisa diperiksa.
 */
export function shopeeFallbackDescription(
  product: ShopeeProductRow,
  plan?: ShopeeTierPlan
): string {
  const name = clean(product.name);
  const brand = clean(product.brand);
  const category = clean(product.category);

  const parts: string[] = [];
  parts.push(brand ? `${name} dari ${brand}.` : `${name}.`);
  if (category) parts.push(`Kategori: ${category}.`);

  const groups = [...(product.variant_groups ?? [])].sort(bySortOrder);
  const facets = groups
    .map((g) => {
      const opts = [...(g.variant_options ?? [])]
        .sort(bySortOrder)
        .map((o) => clean(o.name))
        .filter(Boolean);
      return opts.length > 0 ? `${clean(g.name)}: ${opts.join(", ")}` : "";
    })
    .filter(Boolean);
  if (facets.length > 0) parts.push(`Pilihan tersedia — ${facets.join("; ")}.`);
  if (plan && plan.groupCount === 0) parts.push("Tersedia dalam satu pilihan.");

  return parts.join(" ").slice(0, SHOPEE_LIMITS.descMax);
}

// ── Foto ─────────────────────────────────────────────────────────────────────

/**
 * URL foto produk terurut: SAMPUL dulu lalu sisanya menurut `sort_order`.
 * Urutan pemilihan sampul mencerminkan `pickProductCoverUrl` (is_cover menang →
 * gambar pertama). URL dibangun lewat `resolveProductMediaUrl`, BUKAN
 * `media_url` mentah — kalau tidak, produk hasil restore menunjuk project
 * Supabase lama dan foto tak pernah termuat di Shopee.
 */
export function productImageUrls(
  media: ShopeeMediaRow[] | null | undefined,
  baseUrl?: string | null
): string[] {
  const images = (media ?? []).filter((m) => m?.media_type === "image");
  const sorted = [...images].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
  );
  const coverIdx = sorted.findIndex((m) => m.is_cover === true);
  const ordered =
    coverIdx > 0
      ? [sorted[coverIdx], ...sorted.filter((_, i) => i !== coverIdx)]
      : sorted;
  const urls: string[] = [];
  for (const row of ordered) {
    const url = clean(resolveProductMediaUrl(row, baseUrl));
    if (url && !urls.includes(url)) urls.push(url);
  }
  return urls;
}

// ── Penyusunan baris ─────────────────────────────────────────────────────────

type PendingRow = {
  tier1?: ShopeeTierOption;
  tier2?: ShopeeTierOption;
  variant: ShopeeVariantRow;
  price: number;
};

function buildProductBlock(
  product: ShopeeProductRow,
  opts: ShopeeBuildOptions,
  warn: (kind: ShopeeWarningKind, message: string) => void
): ShopeeProductBlock | null {
  const plan = planVariantTiers(product.variant_groups);
  const activeVariants = (product.product_variants ?? []).filter(
    (v) => v.is_active !== false
  );

  // Indeks kombinasi → varian. INI inti kebenaran SKU: kuncinya `option_ids`,
  // bukan `name`.
  const byOptions = new Map<string, ShopeeVariantRow>();
  for (const v of activeVariants) {
    const ids = v.option_ids ?? [];
    if (ids.length === 0) continue;
    const key = optionKey(ids);
    if (!byOptions.has(key)) byOptions.set(key, v);
  }

  const pending: PendingRow[] = [];
  const usedVariants = new Set<ShopeeVariantRow>();
  let missingCombo = 0;
  let missingPrice = 0;

  const accept = (
    variant: ShopeeVariantRow,
    tier1?: ShopeeTierOption,
    tier2?: ShopeeTierOption
  ) => {
    // mig 7900: harga file = dasar + tambahan (XXL terdaftar dengan harga utuhnya).
    const base = shopeeVariantPrice(variant);
    if (base === null || base <= 0) {
      missingPrice += 1;
      return;
    }
    // Hook harga (lihat ShopeeBuildOptions.priceTransform). Hasil yang tak
    // masuk akal (NaN/≤0) diperlakukan seperti harga kosong — dilaporkan,
    // bukan ditulis sebagai angka aneh ke Shopee.
    const price = opts.priceTransform ? opts.priceTransform(base) : base;
    if (!Number.isFinite(price) || price <= 0) {
      missingPrice += 1;
      return;
    }
    usedVariants.add(variant);
    pending.push({ tier1, tier2, variant, price });
  };

  if (plan.groupCount === 0) {
    // Tanpa grup varian. Umumnya satu varian bernama "Standar" → satu baris
    // tanpa kolom variasi sama sekali. Bentuk warisan (banyak varian tanpa
    // grup) tetap bisa diexport dengan NAMA varian sebagai pilihan tingkat 1 —
    // SKU-nya tetap persis karena barisnya diambil dari varian itu langsung.
    const ordered = [...activeVariants].sort(bySortOrder);
    if (ordered.length === 1) {
      accept(ordered[0]);
    } else {
      const labels = uniqueLabels(
        ordered.map((v) =>
          fitLabel([v.name], "", SHOPEE_LIMITS.optionNameMax)
        ),
        SHOPEE_LIMITS.optionNameMax
      );
      ordered.forEach((v, i) => {
        accept(v, { label: labels[i], optionIds: [], imageUrl: null });
      });
    }
  } else {
    const tier2List: (ShopeeTierOption | undefined)[] =
      plan.tier2.length > 0 ? plan.tier2 : [undefined];
    for (const t1 of plan.tier1) {
      for (const t2 of tier2List) {
        const ids = t2 ? [...t1.optionIds, ...t2.optionIds] : t1.optionIds;
        const variant = byOptions.get(optionKey(ids));
        if (!variant) {
          missingCombo += 1;
          continue;
        }
        accept(variant, t1, t2);
      }
    }
  }

  if (missingCombo > 0) {
    warn(
      "kombinasi_tanpa_varian",
      `${missingCombo} kombinasi varian tidak punya baris di database — dilewati (SKU tidak dikarang).`
    );
  }
  if (missingPrice > 0) {
    warn(
      "harga_kosong",
      `${missingPrice} varian dilewati karena harga kosong — Shopee mewajibkan harga ${SHOPEE_LIMITS.priceMin}–${SHOPEE_LIMITS.priceMax}.`
    );
  }

  const unused = activeVariants.filter((v) => !usedVariants.has(v));
  if (plan.groupCount > 0 && unused.length > 0) {
    const withPrice = unused.filter((v) => shopeeVariantPrice(v) !== null);
    if (withPrice.length > 0) {
      warn(
        "varian_tak_terpakai",
        `${withPrice.length} varian berharga tidak masuk file karena option_ids-nya tidak cocok dengan grup varian produk ini.`
      );
    }
  }

  if (pending.length === 0) {
    warn(
      "tanpa_varian_layak",
      "Produk dilewati — tidak ada varian yang punya harga dan kombinasi yang cocok."
    );
    return null;
  }

  if (pending.length > SHOPEE_MAX_ROWS_PER_FILE) {
    warn(
      "produk_terlalu_besar",
      `Produk dilewati — ${pending.length} kombinasi melebihi ${SHOPEE_MAX_ROWS_PER_FILE} baris per file, dan jumlah sebanyak itu juga di atas batas model Shopee.`
    );
    return null;
  }

  // ── Pemeriksaan tingkat produk ─────────────────────────────────────────────
  const prices = pending.map((p) => p.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  if (minPrice < SHOPEE_LIMITS.priceMin || maxPrice > SHOPEE_LIMITS.priceMax) {
    warn(
      "harga_di_luar_batas",
      `Ada harga di luar batas Shopee (${SHOPEE_LIMITS.priceMin}–${SHOPEE_LIMITS.priceMax}): terendah ${minPrice}, tertinggi ${maxPrice}.`
    );
  }
  if (minPrice > 0 && maxPrice / minPrice > SHOPEE_LIMITS.priceRatioMax) {
    warn(
      "rasio_harga",
      `Harga termahal ÷ termurah = ${(maxPrice / minPrice).toFixed(1)}× — Shopee membatasi ${SHOPEE_LIMITS.priceRatioMax}× dalam satu produk.`
    );
  }

  const skus = pending.map((p) => clean(p.variant.sku));
  if (skus.some((s) => s === "")) {
    warn(
      "sku_kosong",
      `${skus.filter((s) => s === "").length} varian tidak punya SKU — kolom Kode Variasi dikosongkan.`
    );
  }
  const dupSku = new Set(
    skus.filter((s, i) => s !== "" && skus.indexOf(s) !== i)
  );
  if (dupSku.size > 0) {
    warn(
      "sku_ganda",
      `SKU berulang dalam satu produk: ${[...dupSku].join(", ")}.`
    );
  }

  const name = clean(product.name);
  if (name.length < SHOPEE_LIMITS.nameMin) {
    warn(
      "nama_terlalu_pendek",
      `Nama produk hanya ${name.length} karakter — Shopee mewajibkan minimal ${SHOPEE_LIMITS.nameMin}.`
    );
  }

  let description = clean(product.description);
  if (description.length < SHOPEE_LIMITS.descMin) {
    description = shopeeFallbackDescription(product, plan);
    if (description.length < SHOPEE_LIMITS.descMin) {
      warn(
        "deskripsi_pendek",
        `Deskripsi hanya ${description.length} karakter — Shopee mewajibkan minimal ${SHOPEE_LIMITS.descMin}. Isi manual sebelum upload.`
      );
    } else {
      warn(
        "deskripsi_otomatis",
        "Deskripsi kosong/terlalu pendek — diisi otomatis dari data produk. Periksa sebelum upload."
      );
    }
  }
  description = description.slice(0, SHOPEE_LIMITS.descMax);

  const images = productImageUrls(product.product_media, opts.baseUrl);
  if (images.length === 0) {
    warn(
      "foto_sampul_kosong",
      "Produk tidak punya foto — kolom Foto Sampul wajib diisi Shopee."
    );
  }

  // Foto per-varian hanya boleh dipakai kalau SEMUA pilihan tingkat 1 punya
  // foto (aturan Panduan Shopee: isi semua atau tidak sama sekali).
  const tier1WithImages =
    plan.imageGroupInTier1 &&
    plan.tier1.length > 0 &&
    plan.tier1.every((t) => clean(t.imageUrl).length > 0);
  if (plan.groupCount > 0 && plan.imageGroupInTier1 && !tier1WithImages) {
    warn(
      "foto_varian_tak_lengkap",
      "Foto per-varian dikosongkan — Shopee menuntut semua pilihan Variasi 1 berfoto, sebagian belum."
    );
  }

  const isPreorder = product.product_type === "preorder";
  let missingWeight = 0;

  const rows: ShopeeRowValues[] = pending.map((p, i) => {
    const first = i === 0;
    const weightGram = toNum(p.variant.weight);
    if (weightGram === null || weightGram <= 0) missingWeight += 1;

    // Stok: ready stock pakai stok nyata. Preorder tak punya stok fisik —
    // pakai kuota PO bila produknya memang berkuota, selain itu konstanta.
    const rawStock = isPreorder
      ? product.po_limited === true
        ? (p.variant.po_quota ?? SHOPEE_PREORDER_DEFAULT_STOCK)
        : SHOPEE_PREORDER_DEFAULT_STOCK
      : (p.variant.stock ?? 0);
    // Hook stok hanya menyentuh READY STOCK (lihat ShopeeBuildOptions).
    const stock =
      !isPreorder && opts.stockTransform ? opts.stockTransform(rawStock) : rawStock;

    const values: ShopeeRowValues = {
      // Kategori sengaja DIKOSONGKAN (kolom "Opsional"): Shopee merekomendasikan
      // kategori sendiri saat file diupload.
      ps_category: null,
      // Nama & deskripsi HANYA di baris pertama produk — petunjuk template
      // menyatakannya eksplisit ("kosongkan untuk variasi lainnya").
      ps_product_name: first ? name.slice(0, SHOPEE_LIMITS.nameMax) : null,
      ps_product_description: first ? description : null,
      ps_sku_parent_short: clean(product.sku_prefix).slice(0, SHOPEE_LIMITS.skuMax) || null,
      ps_dangerous_goods: SHOPEE_NOT_DANGEROUS,
      ps_price: p.price,
      ps_stock: Math.max(0, Math.min(SHOPEE_LIMITS.stockMax, Math.round(stock))),
      ps_sku_short: clean(p.variant.sku).slice(0, SHOPEE_LIMITS.skuMax) || null,
      // Berat dalam GRAM apa adanya — satuan kolom Berat template Shopee
      // ("Berat (gram) produk menunjukkan berat paket", sheet Panduan), sama
      // dengan satuan `product_variants.weight`. JANGAN dibagi 1000: berat
      // 1/1000 dari sebenarnya membuat Shopee menagih ongkir jauh di bawah
      // biaya kirim asli, dan selisihnya ditanggung toko.
      ps_weight:
        weightGram !== null && weightGram > 0
          ? weightGram
          : SHOPEE_DEFAULT_WEIGHT_GRAM,
      // Foto produk diulang di SETIAP baris — mengikuti sheet "Contoh Upload"
      // template, yang menuliskan link sampul yang sama di tiap baris varian.
      ps_item_cover_image: images[0] ?? null,
    };
    for (let n = 1; n <= 8; n++) values[`ps_item_image_${n}`] = images[n] ?? null;
    for (const code of opts.channelCodes ?? []) {
      values[code] = SHOPEE_CHANNEL_ACTIVE;
    }

    if (p.tier1) {
      // Kode integrasi menyatukan baris-baris ini jadi SATU produk di Shopee.
      values.et_title_variation_integration_no =
        clean(product.sku_prefix).slice(0, SHOPEE_LIMITS.skuMax) || product.id;
      values.et_title_variation_1 =
        plan.groupCount === 0 ? SHOPEE_FALLBACK_VARIATION_NAME : plan.tier1Name;
      values.et_title_option_for_variation_1 = p.tier1.label;
      if (tier1WithImages) {
        values.et_title_image_per_variation = p.tier1.imageUrl;
      }
      if (p.tier2) {
        values.et_title_variation_2 = plan.tier2Name;
        values.et_title_option_for_variation_2 = p.tier2.label;
      }
    }
    return values;
  });

  if (missingWeight > 0) {
    warn(
      "berat_kosong",
      `${missingWeight} varian tanpa berat — diisi ${SHOPEE_DEFAULT_WEIGHT_GRAM} g bawaan. Periksa sebelum upload: berat salah membuat ongkir salah.`
    );
  }

  return { productId: product.id, productName: name, rows };
}

/** Susun seluruh baris Shopee dari daftar produk. Tidak pernah menulis apa pun. */
export function buildShopeeRows(
  products: ShopeeProductRow[],
  opts: ShopeeBuildOptions = {}
): ShopeeBuildResult {
  const warnings: ShopeeWarning[] = [];
  const blocks: ShopeeProductBlock[] = [];

  for (const product of products) {
    const block = buildProductBlock(product, opts, (kind, message) => {
      warnings.push({
        productId: product.id,
        productName: clean(product.name),
        kind,
        message,
      });
    });
    if (block) blocks.push(block);
  }

  return {
    blocks,
    warnings,
    productCount: blocks.length,
    rowCount: blocks.reduce((n, b) => n + b.rows.length, 0),
  };
}

// ── Pemecahan file ───────────────────────────────────────────────────────────

/**
 * Pecah blok produk jadi beberapa file.
 *
 * Baris satu produk TIDAK PERNAH terbelah antar file: Shopee menyatukan baris
 * jadi satu produk lewat `et_title_variation_integration_no`, jadi produk yang
 * terbelah akan terupload sebagai DUA produk berbeda dengan variasi terpotong.
 */
export function chunkProductBlocks(
  blocks: ShopeeProductBlock[],
  maxRows: number = SHOPEE_MAX_ROWS_PER_FILE
): ShopeeRowValues[][] {
  const limit = Math.max(1, Math.floor(maxRows));
  const files: ShopeeRowValues[][] = [];
  let current: ShopeeRowValues[] = [];
  for (const block of blocks) {
    if (current.length > 0 && current.length + block.rows.length > limit) {
      files.push(current);
      current = [];
    }
    current.push(...block.rows);
  }
  if (current.length > 0) files.push(current);
  return files;
}

// ── Nama file ────────────────────────────────────────────────────────────────

function stamp(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** Awalan nama unduhan export admin. Jalur lain (Dropship Shopee) memberi awalannya sendiri. */
export const SHOPEE_FILE_PREFIX = "shopee-produk";

/** Nama satu file .xlsx; `total > 1` menambahkan penanda urutan. */
export function shopeeFileName(
  index: number,
  total: number,
  date: Date = new Date(),
  prefix: string = SHOPEE_FILE_PREFIX
): string {
  const suffix = total > 1 ? `-${index + 1}dari${total}` : "";
  return `${prefix}-${stamp(date)}${suffix}.xlsx`;
}

/** Nama bundel .zip saat file lebih dari satu. */
export function shopeeBundleName(
  date: Date = new Date(),
  prefix: string = SHOPEE_FILE_PREFIX
): string {
  return `${prefix}-${stamp(date)}.zip`;
}

// ── Ringkasan peringatan untuk pratinjau ─────────────────────────────────────

export type ShopeeWarningSummary = {
  kind: ShopeeWarningKind;
  /** Kalimat siap tampil di modal pratinjau. */
  label: string;
  /** Jumlah PRODUK yang kena, bukan jumlah baris. */
  productCount: number;
  /** Beberapa nama produk sebagai contoh (maks 3). */
  sampleProducts: string[];
  /**
   * Id produk contoh, SEJAJAR indeks dengan `sampleProducts` — panel admin
   * menautkan tiap nama ke form edit produknya. Field terpisah (bukan
   * mengganti `sampleProducts`) supaya jalur Dropship mitra tak berubah.
   */
  sampleProductIds: string[];
};

const WARNING_LABELS: Record<ShopeeWarningKind, string> = {
  tanpa_varian_layak: "produk dilewati — tak ada varian berharga yang cocok",
  kombinasi_tanpa_varian: "produk punya kombinasi tanpa baris varian (dilewati)",
  harga_kosong: "produk punya varian tanpa harga (dilewati)",
  harga_di_luar_batas: "produk berharga di luar batas Shopee",
  rasio_harga: "produk melewati batas rasio harga 7× Shopee",
  berat_kosong: `produk punya varian tanpa berat (diisi ${SHOPEE_DEFAULT_WEIGHT_GRAM} g bawaan)`,
  sku_kosong: "produk punya varian tanpa SKU",
  deskripsi_otomatis: "produk memakai deskripsi otomatis dari data produk",
  deskripsi_pendek: "produk berdeskripsi di bawah 20 karakter",
  nama_terlalu_pendek: "produk bernama di bawah 5 karakter",
  foto_sampul_kosong: "produk tanpa foto sama sekali",
  foto_varian_tak_lengkap: "produk berfoto varian tak lengkap (dikosongkan)",
  varian_tak_terpakai: "produk punya varian yang option_ids-nya tak cocok grup",
  produk_terlalu_besar: "produk dilewati — kombinasinya terlalu banyak",
  sku_ganda: "produk punya SKU berulang di dalam dirinya",
};

/**
 * Kelompokkan peringatan per jenis untuk modal pratinjau. Diurut dari yang
 * paling banyak kena supaya yang paling perlu diperiksa muncul di atas;
 * seri dipecah nama jenis agar hasilnya deterministik.
 */
export function summarizeShopeeWarnings(
  warnings: ShopeeWarning[]
): ShopeeWarningSummary[] {
  const byKind = new Map<ShopeeWarningKind, Set<string>>();
  const names = new Map<string, string>();
  for (const w of warnings) {
    if (!byKind.has(w.kind)) byKind.set(w.kind, new Set());
    byKind.get(w.kind)!.add(w.productId);
    names.set(w.productId, w.productName);
  }
  return [...byKind.entries()]
    .map(([kind, ids]) => {
      const sample = [...ids].slice(0, 3);
      return {
        kind,
        label: WARNING_LABELS[kind],
        productCount: ids.size,
        sampleProducts: sample.map((id) => names.get(id) ?? id),
        sampleProductIds: sample,
      };
    })
    .sort((a, b) => b.productCount - a.productCount || a.kind.localeCompare(b.kind));
}
